import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '../contexts/WalletContext';
import { useNetwork } from '../contexts/NetworkContext';
import { useEscrow } from '../contexts/EscrowContext';
import Header from './Header';
import ContractAddressInput from './ContractAddressInput';
import './HomePage.css';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { isConnected, loading, signer, account } = useWallet();
  const { currentNetwork } = useNetwork();
  const { isAddressSet } = useEscrow();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (isConnected && signer && currentNetwork && account) {
      setIsInitialized(true);
    } else if (!isConnected && !loading) {
      // If not connected and not loading, show connect prompt
      setIsInitialized(true);
    } else {
      // Reset states when not connected or missing dependencies
      setIsInitialized(false);
    }
  }, [isConnected, signer, currentNetwork, account, loading]);

  // Check if user is connected but on unsupported network
  // This happens when user is connected but currentNetwork is null (unsupported network)
  const isUnsupportedNetwork = isConnected && !loading && signer && account && !currentNetwork;

  // Auto-navigate to admin page when wallet is connected and address is set
  useEffect(() => {
    if (isConnected && isAddressSet && isInitialized) {
      navigate('/manage');
    }
  }, [isConnected, isAddressSet, isInitialized, navigate]);


  return (
    <div className="home-page">
      <Header />
      
      {loading ? (
        <div className="home-container">
          <div className="loading">
            Connecting to wallet...
          </div>
        </div>
      ) : isUnsupportedNetwork ? (
        <div className="home-container">
          <div className="unsupported-network">
            <div className="error-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2"/>
                <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>
            <h2 className="error-title">Unsupported Network</h2>
            <p className="error-description">
              You are connected to an unsupported network. Please switch to a supported network to use this application.
            </p>
            <p className="error-subtitle">
              Supported networks include: Polygon, Arbitrum, Optimism, Avalanche, Base, and Sepolia.
            </p>
            <p className="error-instruction">
              Use the network selector in the header above to switch networks, or change your network in your wallet.
            </p>
          </div>
        </div>
      ) : !isInitialized ? (
        <div className="home-container">
          <div className="loading">
            Initializing...
          </div>
        </div>
      ) : isConnected && !isAddressSet ? (
        <ContractAddressInput />
      ) : isConnected && isAddressSet ? (
        <div className="home-container">
          <div className="loading">
            Redirecting to manage page...
          </div>
        </div>
      ) : (
        <div className="home-container">
          <div className="connect-prompt">
            <div className="connect-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="connect-title">Connect Your Wallet</h2>
            <p className="connect-description">
              Please connect your wallet to access the TrustLine escrow platform and start managing your deposits.
            </p>
            <p className="connect-subtitle">
              Use the "Connect Wallet" button in the header above to get started.
            </p>
          </div>
        </div>
      )}
      
      <footer>
        <p>Contact us: <a href="mailto:contact@trustline.id">contact@trustline.id</a></p>
      </footer>
    </div>
  );
};

export default HomePage;
